import { Spinner } from "@/components/ui/Spinner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Eye,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { fetchWithAuth } from "../../../hooks/useAuth";

interface ProductOptionValue {
  id: number;
  value: string;
  position?: number;
}

interface ProductOption {
  id: number;
  productId: number;
  name: string;
  position?: number;
  values: ProductOptionValue[];
}

async function parseOptionsResponse(res: Response): Promise<ProductOption[]> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Eroare la încărcarea opțiunilor");
  }
  const result = await res.json();
  if (result && Array.isArray(result.date))
    return result.date as ProductOption[];
  if (result && Array.isArray(result.data))
    return result.data as ProductOption[];
  if (Array.isArray(result)) return result as ProductOption[];
  return [];
}

export default function ProductOptionsPannel({
  productId,
}: {
  productId: number;
}) {
  const queryClient = useQueryClient();

  const { data: options, isLoading } = useQuery<ProductOption[]>({
    queryKey: ["product-options", productId],
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/options`
      );
      return parseOptionsResponse(res);
    },
  });

  type VariantLike = {
    id: number;
    optionValues?: Array<{ valueId: number }>;
    option_values?: Array<{ valueId: number }>;
    values?: Array<{ valueId: number }>;
  };
  const { data: variantsForKeys } = useQuery<VariantLike[]>({
    queryKey: ["product-variants", productId, "for-keys"],
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/variants`
      );
      if (!res.ok) return [] as VariantLike[];
      const json = await res.json();
      if (Array.isArray(json)) return json as VariantLike[];
      if (json && Array.isArray(json.date)) return json.date as VariantLike[];
      if (json && Array.isArray(json.variants))
        return json.variants as VariantLike[];
      return [] as VariantLike[];
    },
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["product-options", productId],
    });
  };

  const createOption = useMutation({
    mutationFn: async (payload: {
      name: string;
      position?: number;
      values?: string[];
    }) => {
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/options`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la crearea opțiunii");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast.success("Opțiune creată");
      await invalidate();
      setNewOptionName("");
      setNewOptionValues("");
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const updateOption = useMutation({
    mutationFn: async ({
      optionId,
      data,
    }: {
      optionId: number;
      data: { name?: string; position?: number };
    }) => {
      const res = await fetchWithAuth(
        `/api/admin/products/options/${optionId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la actualizarea opțiunii");
      }
      return res.json();
    },
    onSuccess: async () => {
      await invalidate();
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const deleteOption = useMutation({
    mutationFn: async (optionId: number) => {
      const res = await fetchWithAuth(
        `/api/admin/products/options/${optionId}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la ștergerea opțiunii");
      }
      return true;
    },
    onSuccess: async () => {
      toast.success("Opțiune ștearsă");
      await invalidate();
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const addValue = useMutation({
    mutationFn: async ({
      optionId,
      value,
      position,
    }: {
      optionId: number;
      value: string;
      position?: number;
    }) => {
      const res = await fetchWithAuth(
        `/api/admin/products/options/${optionId}/values`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ value, position }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la adăugarea valorii");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast.success("Valoare adăugată");
      await invalidate();
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const updateValue = useMutation({
    mutationFn: async ({
      valueId,
      data,
    }: {
      valueId: number;
      data: { value?: string; position?: number };
    }) => {
      const res = await fetchWithAuth(
        `/api/admin/products/option-values/${valueId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la actualizarea valorii");
      }
      return res.json();
    },
    onSuccess: async () => {
      await invalidate();
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const deleteValue = useMutation({
    mutationFn: async (valueId: number) => {
      const res = await fetchWithAuth(
        `/api/admin/products/option-values/${valueId}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la ștergerea valorii");
      }
      return true;
    },
    onSuccess: async () => {
      toast.success("Valoare ștearsă");
      await invalidate();
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const syncPreview = useMutation({
    mutationFn: async () => {
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/variants/sync`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ apply: false }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la previzualizarea variantelor");
      }
      return res.json();
    },
    onSuccess: (res: any) => {
      const data = res?.data || res?.date || res;
      const desired = computeDesiredCombos(options || []);
      const currentKeys = new Set<string>(
        (variantsForKeys || []).map((v) => toKey(extractValueIds(v)))
      );
      const toCreateList = desired
        .filter((combo) => !currentKeys.has(toKey(combo.valueIds)))
        .map((combo) => ({ ids: combo.valueIds, label: combo.label }));
      setComputedPreview({
        toCreate: toCreateList,
        toCreateCount: toCreateList.length,
        server: data,
      });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const syncApply = useMutation({
    mutationFn: async () => {
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/variants/sync`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ apply: true }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la sincronizarea variantelor");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast.success("Variante sincronizate");
      setComputedPreview(null);
      await queryClient.invalidateQueries({
        queryKey: ["product", String(productId)],
      });
      await invalidate();
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const [newOptionName, setNewOptionName] = React.useState("");
  const [newOptionValues, setNewOptionValues] = React.useState("");
  const [editingNames, setEditingNames] = React.useState<
    Record<number, string>
  >({});
  const [valueEditors, setValueEditors] = React.useState<
    Record<number, { value: string }>
  >({});
  const [computedPreview, setComputedPreview] = React.useState<{
    toCreate: Array<{ ids: number[]; label: string }>;
    toCreateCount: number;
    server: any;
  } | null>(null);

  const moveOption = async (option: ProductOption, direction: -1 | 1) => {
    if (!options) return;
    const sorted = [...options].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id
    );
    const idx = sorted.findIndex((o) => o.id === option.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    const posA = option.position ?? idx + 1;
    const posB = other.position ?? swapIdx + 1;
    await Promise.all([
      updateOption.mutateAsync({
        optionId: option.id,
        data: { position: posB },
      }),
      updateOption.mutateAsync({
        optionId: other.id,
        data: { position: posA },
      }),
    ]);
  };

  const moveValue = async (
    option: ProductOption,
    value: ProductOptionValue,
    direction: -1 | 1
  ) => {
    const sortedVals = [...(option.values || [])].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id
    );
    const idx = sortedVals.findIndex((v) => v.id === value.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sortedVals.length) return;
    const other = sortedVals[swapIdx];
    const posA = value.position ?? idx + 1;
    const posB = other.position ?? swapIdx + 1;
    await Promise.all([
      updateValue.mutateAsync({ valueId: value.id, data: { position: posB } }),
      updateValue.mutateAsync({ valueId: other.id, data: { position: posA } }),
    ]);
  };

  return (
    <Card>
      <CardHeader>
        {computedPreview && (
          <div className="mb-3 border rounded p-3 bg-muted/30">
            <div className="text-sm font-medium mb-2">
              Preview modificări variante
            </div>
            <div className="text-sm">
              De creat:{" "}
              <Badge variant="outline">{computedPreview.toCreateCount}</Badge>
            </div>
            {computedPreview.toCreate.length > 0 && (
              <div className="mt-2 max-h-56 overflow-auto text-sm space-y-1">
                {computedPreview.toCreate.map((c, idx) => (
                  <div key={idx} className="text-foreground">
                    {c.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <CardTitle className="flex items-center justify-between">
          <span>Opțiuni Produs</span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => syncPreview.mutate()}
              disabled={syncPreview.isPending}
              className="flex items-center gap-2"
            >
              {syncPreview.isPending ? (
                <Spinner className="w-4 h-4 animate-spin" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
              Previzualizeaza Variante
            </Button>
            <Button
              onClick={() => syncApply.mutate()}
              disabled={syncApply.isPending}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {syncApply.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Creaza variante
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-sm text-muted-foreground">
                Nume opțiune
              </label>
              <Input
                placeholder="ex: Culoare"
                value={newOptionName}
                onChange={(e) => setNewOptionName(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-muted-foreground">
                Valori inițiale (separate prin virgulă)
              </label>
              <Input
                placeholder="ex: Roșu, Albastru, Negru"
                value={newOptionValues}
                onChange={(e) => setNewOptionValues(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => {
                if (!newOptionName.trim()) {
                  toast.error("Completează numele opțiunii");
                  return;
                }
                const valuesArr = newOptionValues
                  .split(",")
                  .map((v) => v.trim())
                  .filter(Boolean);
                createOption.mutate({
                  name: newOptionName.trim(),
                  values: valuesArr,
                });
              }}
              disabled={createOption.isPending}
              className="flex items-center gap-2"
            >
              {createOption.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Adaugă opțiune
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Se încarcă opțiunile...
          </div>
        ) : !options || options.length === 0 ? (
          <div className="text-muted-foreground">Nu există opțiuni</div>
        ) : (
          <div className="space-y-4">
            {options
              .slice()
              .sort(
                (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id
              )
              .map((opt, optIndex, arr) => (
                <Card key={opt.id} className="border">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between text-base">
                      <div className="flex items-center gap-3 w-full">
                        <Input
                          value={editingNames[opt.id] ?? opt.name}
                          onChange={(e) =>
                            setEditingNames((prev) => ({
                              ...prev,
                              [opt.id]: e.target.value,
                            }))
                          }
                          onBlur={() => {
                            const name = (
                              editingNames[opt.id] ?? opt.name
                            ).trim();
                            if (name && name !== opt.name) {
                              updateOption.mutate({
                                optionId: opt.id,
                                data: { name },
                              });
                            }
                          }}
                        />
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveOption(opt, -1)}
                            disabled={optIndex === 0}
                            className="px-2"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveOption(opt, 1)}
                            disabled={optIndex === arr.length - 1}
                            className="px-2"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          if (window.confirm("Sigur ștergi această opțiune?")) {
                            deleteOption.mutate(opt.id);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {opt.values.length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        Nu există valori
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {opt.values
                          .slice()
                          .sort(
                            (a, b) =>
                              (a.position ?? 0) - (b.position ?? 0) ||
                              a.id - b.id
                          )
                          .map((val, valIndex, vals) => (
                            <div
                              key={val.id}
                              className="flex items-center gap-2"
                            >
                              <Input
                                value={valueEditors[val.id]?.value ?? val.value}
                                onChange={(e) =>
                                  setValueEditors((prev) => ({
                                    ...prev,
                                    [val.id]: { value: e.target.value },
                                  }))
                                }
                                onBlur={() => {
                                  const value = (
                                    valueEditors[val.id]?.value ?? val.value
                                  ).trim();
                                  if (value && value !== val.value) {
                                    updateValue.mutate({
                                      valueId: val.id,
                                      data: { value },
                                    });
                                  }
                                }}
                              />
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => moveValue(opt, val, -1)}
                                  disabled={valIndex === 0}
                                  className="px-2"
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => moveValue(opt, val, 1)}
                                  disabled={valIndex === vals.length - 1}
                                  className="px-2"
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </Button>
                              </div>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => deleteValue.mutate(val.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Add value */}
                    <AddValueRow
                      onAdd={async (value) => {
                        if (!value.trim()) return;
                        await addValue.mutateAsync({
                          optionId: opt.id,
                          value: value.trim(),
                        });
                      }}
                    />
                  </CardContent>
                </Card>
              ))}
          </div>
        )}

        {/* preview moved to header */}
      </CardContent>
    </Card>
  );
}

function AddValueRow({
  onAdd,
}: {
  onAdd: (value: string) => void | Promise<void>;
}) {
  const [value, setValue] = React.useState("");
  const [isAdding, setIsAdding] = React.useState(false);
  return (
    <div className="flex items-center gap-2">
      <Input
        placeholder="Adaugă valoare (ex: M)"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={async (e) => {
          if (e.key === "Enter" && value.trim()) {
            setIsAdding(true);
            await onAdd(value);
            setValue("");
            setIsAdding(false);
          }
        }}
      />
      <Button
        onClick={async () => {
          if (!value.trim()) return;
          setIsAdding(true);
          await onAdd(value);
          setValue("");
          setIsAdding(false);
        }}
        disabled={isAdding}
        className="flex items-center gap-2"
      >
        {isAdding ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Plus className="w-4 h-4" />
        )}
        Adaugă
      </Button>
    </div>
  );
}

// Helpers to compute desired combinations and labels
function toKey(ids: number[]): string {
  return ids
    .slice()
    .sort((a, b) => a - b)
    .join("-");
}

function extractValueIds(v: any): number[] {
  const list = v?.optionValues || v?.option_values || v?.values || [];
  return (list as Array<{ valueId: number }>)
    .map((x) => x.valueId)
    .filter((x) => typeof x === "number");
}

function computeDesiredCombos(
  options: ProductOption[]
): Array<{ valueIds: number[]; label: string }> {
  const sortedOptions = (options || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id);
  const valuesByOpt = sortedOptions.map((o) =>
    (o.values || [])
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id)
  );
  const results: Array<{ valueIds: number[]; label: string }> = [];
  const backtrack = (idx: number, currentIds: number[], parts: string[]) => {
    if (idx === sortedOptions.length) {
      results.push({
        valueIds: currentIds.slice(),
        label: parts.join(" / ") || "Default",
      });
      return;
    }
    const opt = sortedOptions[idx];
    const values = valuesByOpt[idx];
    if (!values || values.length === 0) {
      backtrack(idx + 1, currentIds, parts);
      return;
    }
    for (const v of values) {
      currentIds.push(v.id);
      parts.push(`${opt.name}: ${v.value}`);
      backtrack(idx + 1, currentIds, parts);
      parts.pop();
      currentIds.pop();
    }
  };
  if (sortedOptions.length > 0) backtrack(0, [], []);
  return results;
}
